import { useEffect, useMemo, useState } from 'react';
import { describeChange } from '../../core/audit';
import { watchAuditLog, type AuditEntryRecord } from '../../data/audit';

/** Collections that ever produce an entry. Creates are not logged (FR-AUD-01). */
const COLLECTIONS = [
  'transactions', 'accounts', 'categories', 'loans', 'receivables',
  'recurring', 'tasks', 'backup',
];

const PAGE = 200;

/**
 * The audit log viewer (FR-AUD-06).
 *
 * Read-only, and there is no path in the app that could make it otherwise — the rules
 * deny update and delete on this collection to everyone including the owner (FR-AUD-05).
 *
 * Filtering is by collection and free text. Date filtering is deliberately not a server
 * query: the log is the one collection with no retention policy (FR-AUD-03), so it is
 * bounded by a row limit instead and narrowed in memory, the same trade SD-08 makes
 * everywhere else.
 */
export function AuditScreen() {
  const [entries, setEntries] = useState<AuditEntryRecord[] | null>(null);
  const [collectionFilter, setCollectionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEntries(null);
    return watchAuditLog(
      { collection: collectionFilter || undefined, limit: PAGE },
      (rows) => {
        setEntries(rows);
        setError(null);
      },
      (cause) => {
        setEntries([]);
        setError(cause.message);
      },
    );
  }, [collectionFilter]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return entries ?? [];
    return (entries ?? []).filter((entry) =>
      [entry.by, entry.documentId, entry.collection, entry.context ?? '', ...entry.changes.map((c) => c.field)]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [entries, search]);

  return (
    <>
      <div className="head-row">
        <h1>Audit log</h1>
        <span className="muted tiny">
          Edits and deletes only — creates are covered by created-at and entered-by
          (FR-AUD-01). Append-only, kept indefinitely.
        </span>
      </div>

      <div className="filter-bar">
        <div className="row">
          <select
            value={collectionFilter}
            onChange={(e) => setCollectionFilter(e.target.value)}
            aria-label="Record type"
          >
            <option value="">All record types</option>
            {COLLECTIONS.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Who, which record, which field…"
            aria-label="Search the log"
          />
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {entries === null ? (
        <p className="muted">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="placeholder">
          {entries.length === 0 ? 'Nothing has been edited or deleted yet.' : 'Nothing matches that.'}
        </div>
      ) : (
        <>
          <div className="table-scroll">
            <table className="table reclassify-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th>Record</th>
                  <th>Who</th>
                  <th>Changes</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((entry) => (
                  <tr
                    key={entry.id}
                    onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                    className={expanded === entry.id ? 'selected' : undefined}
                  >
                    <td>{entry.at ? entry.at.toLocaleString() : '—'}</td>
                    <td>
                      {entry.action}
                      {entry.context && <span className="badge">{entry.context}</span>}
                    </td>
                    <td>
                      {entry.collection}
                      <div className="muted tiny">{entry.documentId}</div>
                    </td>
                    <td className="muted tiny">{entry.by}</td>
                    <td>
                      {expanded === entry.id ? (
                        // Field-level before and after (FR-AUD-02), which is the whole
                        // reason the log is worth keeping.
                        <ul className="change-list">
                          {entry.changes.map((change, i) => (
                            <li key={i}>{describeChange(change)}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="muted tiny">
                          {entry.changes.length} field{entry.changes.length === 1 ? '' : 's'}
                          {entry.changes.length > 0 && ` · ${entry.changes.map((c) => c.field).slice(0, 3).join(', ')}`}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="muted tiny">
            Showing {visible.length} of the most recent {PAGE}
            {collectionFilter && ` in ${collectionFilter}`}.
            {' '}Entries outlive the records they describe (FR-DEL-03), so a document id
            here may no longer exist.
          </p>
        </>
      )}
    </>
  );
}
